import axios from '../axios'
import {
	CarrierCapability,
	CarrierSummary,
	CarrierType,
	DeviceInstallation,
	Equipment,
	EquipmentService,
	Inspection,
	InspectionPhoto
} from '../helpers/types/inventory'

// ──────────────────────────────────────
// Carrier Summary
// ──────────────────────────────────────
export const fetchCarrierSummary = async (carrierId: number): Promise<CarrierSummary> => {
	const { data } = await axios.get(`/api/v1/carrier/${carrierId}/summary`)
	return data
}

// ──────────────────────────────────────
// Carrier Types
// ──────────────────────────────────────
export const fetchCarrierTypes = async (): Promise<CarrierType[]> => {
	const { data } = await axios.get('/api/v1/carrier-type')
	return data
}

export const createCarrierType = async (body: Partial<CarrierType>): Promise<CarrierType> => {
	const { data } = await axios.post('/api/v1/carrier-type', body)
	return data
}

export const updateCarrierType = async (id: number, body: Partial<CarrierType>): Promise<CarrierType> => {
	const { data } = await axios.put(`/api/v1/carrier-type/${id}`, body)
	return data
}

export const deleteCarrierType = async (id: number): Promise<void> => {
	await axios.delete(`/api/v1/carrier-type/${id}`)
}

// ──────────────────────────────────────
// Carrier Capability
// ──────────────────────────────────────
export const fetchCarrierCapability = async (carrierId: number): Promise<CarrierCapability> => {
	const { data } = await axios.get(`/api/v1/carrier-capability/${carrierId}`)
	return data
}

export const upsertCarrierCapability = async (
	carrierId: number,
	body: Partial<Omit<CarrierCapability, 'carrierId'>>
): Promise<CarrierCapability> => {
	const { data } = await axios.put(`/api/v1/carrier-capability/${carrierId}`, body)
	return data
}

export const deleteCarrierCapability = async (carrierId: number): Promise<void> => {
	await axios.delete(`/api/v1/carrier-capability/${carrierId}`)
}

// ──────────────────────────────────────
// Equipment
// ──────────────────────────────────────
export const fetchEquipment = async (carrierId: number): Promise<Equipment[]> => {
	const { data } = await axios.get('/api/v1/equipment', { params: { carrierId } })
	return data
}

export const fetchEquipmentById = async (id: number): Promise<Equipment> => {
	const { data } = await axios.get(`/api/v1/equipment/${id}`)
	return data
}

export const createEquipment = async (body: Partial<Equipment>): Promise<Equipment> => {
	const { data } = await axios.post('/api/v1/equipment', body)
	return data
}

export const updateEquipment = async (id: number, body: Partial<Equipment>): Promise<Equipment> => {
	const { data } = await axios.put(`/api/v1/equipment/${id}`, body)
	return data
}

export const deleteEquipment = async (id: number): Promise<void> => {
	await axios.delete(`/api/v1/equipment/${id}`)
}

// ──────────────────────────────────────
// Equipment Service Records
// ──────────────────────────────────────
export const fetchEquipmentServices = async (equipmentId: number): Promise<EquipmentService[]> => {
	const { data } = await axios.get(`/api/v1/equipment/${equipmentId}/services`)
	return data
}

export const createEquipmentService = async (
	equipmentId: number,
	body: Partial<EquipmentService>
): Promise<EquipmentService> => {
	const { data } = await axios.post(`/api/v1/equipment/${equipmentId}/services`, body)
	return data
}

export const deleteEquipmentService = async (equipmentId: number, serviceId: number): Promise<void> => {
	await axios.delete(`/api/v1/equipment/${equipmentId}/services/${serviceId}`)
}

// ──────────────────────────────────────
// Device Installations
// ──────────────────────────────────────
export const fetchDeviceInstallations = async (carrierId: number): Promise<DeviceInstallation[]> => {
	const { data } = await axios.get(`/api/v1/device-installation/carrier/${carrierId}`)
	return data
}

export const createDeviceInstallation = async (body: Partial<DeviceInstallation>): Promise<DeviceInstallation> => {
	const { data } = await axios.post('/api/v1/device-installation', body)
	return data
}

export const updateDeviceInstallation = async (
	id: number,
	body: Partial<DeviceInstallation>
): Promise<DeviceInstallation> => {
	const { data } = await axios.put(`/api/v1/device-installation/${id}`, body)
	return data
}

export const deleteDeviceInstallation = async (id: number): Promise<void> => {
	await axios.delete(`/api/v1/device-installation/${id}`)
}

// ──────────────────────────────────────
// Inspections
// ──────────────────────────────────────
export const fetchInspections = async (carrierId: number): Promise<Inspection[]> => {
	const { data } = await axios.get('/api/v1/inspection', { params: { carrierId } })
	return data
}

export const fetchInspectionById = async (id: number): Promise<Inspection> => {
	const { data } = await axios.get(`/api/v1/inspection/${id}`)
	return data
}

export const createInspection = async (body: Partial<Inspection>): Promise<Inspection> => {
	const { data } = await axios.post('/api/v1/inspection', body)
	return data
}

export const updateInspection = async (id: number, body: Partial<Inspection>): Promise<Inspection> => {
	const { data } = await axios.put(`/api/v1/inspection/${id}`, body)
	return data
}

export const deleteInspection = async (id: number): Promise<void> => {
	await axios.delete(`/api/v1/inspection/${id}`)
}

// ──────────────────────────────────────
// Inspection Photos
// ──────────────────────────────────────
export const uploadInspectionPhoto = async (
	inspectionId: number,
	file: File,
	photoId: string,
	capturedAt?: string,
	notes?: string
): Promise<InspectionPhoto> => {
	const form = new FormData()
	form.append('photo', file)
	form.append('photoId', photoId)
	if (capturedAt) form.append('capturedAt', capturedAt)
	if (notes) form.append('notes', notes)

	const { data } = await axios.post(`/api/v1/inspection/${inspectionId}/photos`, form, {
		headers: { 'Content-Type': 'multipart/form-data' }
	})
	return data
}

export const deleteInspectionPhoto = async (inspectionId: number, photoId: number): Promise<void> => {
	await axios.delete(`/api/v1/inspection/${inspectionId}/photos/${photoId}`)
}
